import React, { useState, useEffect, useRef } from 'react';
import { loadAvailableSongs } from '../utils/songsList';
import './MusicPlayer.css';

const MusicPlayer = ({ onVolumeEasterEgg, isVisible = true }) => {
  const [isPlaying, setIsPlaying] = useState(false); // Iniciar en false
  const [currentSong, setCurrentSong] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [volumeClickCount, setVolumeClickCount] = useState(0);
  const [songClickCount, setSongClickCount] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [availableSongs, setAvailableSongs] = useState([]);
  const [isLoadingSongs, setIsLoadingSongs] = useState(true);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [isShuffled, setIsShuffled] = useState(true); // Shuffle activado por defecto
  const [songHistory, setSongHistory] = useState([]); // Historial de canciones reproducidas
  const [unplayedSongs, setUnplayedSongs] = useState([]); // Canciones no reproducidas en el ciclo actual
  const audioRef = useRef(null);

  // Función para cargar las canciones disponibles
  const loadSongs = async () => {
    setIsLoadingSongs(true);
    try {
      const songs = await loadAvailableSongs();
      setAvailableSongs(songs);
      
      // Buscar la canción específica "Los Retros - Someone To Spend Time With"
      const losRetrosIndex = songs.findIndex(song => 
        song.title.toLowerCase().includes('los retros') && 
        song.title.toLowerCase().includes('someone to spend time')
      );
      
      // Inicializar la lista de canciones no reproducidas
      const songIndices = songs.map((_, index) => index);
      
      // Si encontramos la canción de Los Retros, empezar con ella
      if (losRetrosIndex !== -1) {
        setCurrentSong(losRetrosIndex);
        // Remover Los Retros de la lista de no reproducidas
        setUnplayedSongs(songIndices.filter(index => index !== losRetrosIndex));
        console.log('Empezando con Los Retros - Someone To Spend Time With');
      } else {
        // Si no se encuentra, usar la lógica anterior
        setUnplayedSongs(songIndices);
        if (songs.length > 0 && isShuffled) {
          const randomIndex = Math.floor(Math.random() * songs.length);
          setCurrentSong(randomIndex);
          setUnplayedSongs(prev => prev.filter(index => index !== randomIndex));
        }
        console.log('Los Retros no encontrado, empezando con canción aleatoria');
      }
      
      console.log(`Canciones detectadas: ${songs.length}`, songs.map(s => s.title));
    } catch (error) {
      console.error('Error cargando canciones:', error);
    } finally {
      setIsLoadingSongs(false);
    }
  };

  useEffect(() => {
    loadSongs();
  }, []);

  // Auto-reproducir cuando las canciones se carguen por primera vez
  useEffect(() => {
    if (availableSongs.length > 0 && audioRef.current && isLoadingSongs === false && !hasUserInteracted) {
      // Intentar reproducir automáticamente
      audioRef.current.play().then(() => {
        setIsPlaying(true);
        setHasUserInteracted(true);
      }).catch(e => {
        console.log("Autoplay bloqueado, esperando interacción del usuario");
        // No cambiar isPlaying, mantenerlo en false hasta la interacción
      });
    }
  }, [availableSongs, isLoadingSongs, hasUserInteracted]);

  // Listener para iniciar música automáticamente con cualquier interacción del usuario
  useEffect(() => {
    const handleUserInteraction = () => {
      if (!hasUserInteracted && availableSongs.length > 0 && audioRef.current) {
        setHasUserInteracted(true);
        setIsPlaying(true); // Activar reproducción automáticamente
        audioRef.current.play().catch(e => console.log("Error en interacción:", e));
      }
    };

    // Escuchar clicks y teclas en toda la página
    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);
    document.addEventListener('touchstart', handleUserInteraction); // Para móviles

    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };
  }, [availableSongs, hasUserInteracted]);

  useEffect(() => {
    if (audioRef.current && availableSongs.length > 0) {
      audioRef.current.volume = volume;
      if (isPlaying) {
        audioRef.current.play().catch(e => {
          console.log("Error al reproducir:", e);
          setIsPlaying(false);
        });
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, currentSong, volume, availableSongs]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const updateTime = () => setCurrentTime(audio.currentTime);
      const updateDuration = () => setDuration(audio.duration);
      
      audio.addEventListener('timeupdate', updateTime);
      audio.addEventListener('loadedmetadata', updateDuration);
      audio.addEventListener('durationchange', updateDuration);
      
      return () => {
        audio.removeEventListener('timeupdate', updateTime);
        audio.removeEventListener('loadedmetadata', updateDuration);
        audio.removeEventListener('durationchange', updateDuration);
      };
    }
  }, [currentSong, availableSongs]);

  const togglePlay = () => {
    if (availableSongs.length > 0) {
      setIsPlaying(!isPlaying);
    }
  };

  // Función para obtener una canción aleatoria sin repetición dentro del ciclo
  const getRandomSong = () => {
    if (availableSongs.length <= 1) return 0;
    
    // Si no hay canciones sin reproducir, reiniciar el ciclo
    if (unplayedSongs.length === 0) {
      const allIndices = availableSongs.map((_, index) => index);
      setUnplayedSongs(allIndices);
      
      // Seleccionar aleatoriamente de todas las canciones para el nuevo ciclo
      const randomIndex = Math.floor(Math.random() * availableSongs.length);
      // Remover la seleccionada de la nueva lista de no reproducidas
      setUnplayedSongs(prev => prev.filter(index => index !== randomIndex));
      return randomIndex;
    }
    
    // Seleccionar aleatoriamente de las canciones no reproducidas
    const randomArrayIndex = Math.floor(Math.random() * unplayedSongs.length);
    const selectedSongIndex = unplayedSongs[randomArrayIndex];
    
    // Remover la canción seleccionada de la lista de no reproducidas
    setUnplayedSongs(prev => prev.filter(index => index !== selectedSongIndex));
    
    return selectedSongIndex;
  };

  const nextSong = () => {
    if (availableSongs.length > 1) {
      // Agregar la canción actual al historial antes de cambiar
      setSongHistory(prev => {
        const newHistory = [...prev, currentSong];
        // Limitar el historial a 20 canciones para evitar uso excesivo de memoria
        return newHistory.length > 20 ? newHistory.slice(-20) : newHistory;
      });
      
      if (isShuffled) {
        const nextSongIndex = getRandomSong();
        setCurrentSong(nextSongIndex);
      } else {
        setCurrentSong((prev) => (prev + 1) % availableSongs.length);
      }
    }
  };

  const prevSong = () => {
    if (availableSongs.length > 1) {
      if (songHistory.length > 0) {
        // Si hay historial, volver a la canción anterior
        const previousSong = songHistory[songHistory.length - 1];
        setSongHistory(prev => prev.slice(0, -1)); // Remover la última canción del historial
        setCurrentSong(previousSong);
      } else {
        // Si no hay historial, comportamiento normal
        if (isShuffled) {
          setCurrentSong(getRandomSong());
        } else {
          setCurrentSong((prev) => (prev - 1 + availableSongs.length) % availableSongs.length);
        }
      }
    }
  };

  const handleSongEnd = () => {
    if (availableSongs.length > 1) {
      nextSong();
    } else {
      // Si solo hay una canción, la reproduce en bucle
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    }
  };

  const handleVolumeClick = () => {
    const newCount = volumeClickCount + 1;
    setVolumeClickCount(newCount);
    
    if (newCount === 3) {
      onVolumeEasterEgg("🍟 Vale por papitas - ¡Easter Egg desbloqueado!");
      setVolumeClickCount(0);
    }
  };

  const handleVolumeChange = (e) => {
    setVolume(parseFloat(e.target.value));
  };

  const handleSongTitleClick = () => {
    const newCount = songClickCount + 1;
    setSongClickCount(newCount);
    
    if (newCount === 7) {
      onVolumeEasterEgg("🎵 Vale por una playlist personalizada - ¡Melómano secreto encontrado!");
      setSongClickCount(0);
    }
  };

  const handleProgressClick = (e) => {
    const audio = audioRef.current;
    if (audio && duration) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      const newTime = percent * duration;
      audio.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '00:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Mostrar mensaje de carga si aún no se han detectado canciones
  if (isLoadingSongs) {
    return (
      <div className="music-player">
        <div className="loading-message">
          <span>Detectando canciones... ♪</span>
        </div>
      </div>
    );
  }

  // Mostrar mensaje si no hay canciones
  if (availableSongs.length === 0) {
    return (
      <div className="music-player">
        <div className="no-songs-message">
          <span>No se encontraron canciones. Agrega archivos MP3 a /backend/uploads/music/</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`music-player ${!isVisible ? 'hidden-during-intro' : ''}`}>
      <div className="player-controls">
        <button className="control-btn prev-btn" onClick={prevSong}>
          ◀◀
        </button>
        <button className="control-btn play-btn" onClick={togglePlay}>
          {isPlaying ? '■' : '▶'}
        </button>
        <button className="control-btn next-btn" onClick={nextSong}>
          ▶▶
        </button>
        <div className="volume-control">
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={volume}
            onChange={handleVolumeChange}
            className="volume-slider"
          />
        </div>
      </div>
      <div className="progress-container">
        <span className="time-display">{formatTime(currentTime)}</span>
        <div className="progress-bar" onClick={handleProgressClick}>
          <div 
            className="progress-fill"
            style={{ width: duration ? `${(currentTime / duration) * 100}%` : '0%' }}
          ></div>
        </div>
        <span className="time-display">{formatTime(duration)}</span>
      </div>
      <div className="song-info">
        <span className="song-title" onClick={handleSongTitleClick}>
          {availableSongs[currentSong]?.title || 'Cargando...'}
        </span>
        <div className="song-counter">
          {currentSong + 1} / {availableSongs.length}
        </div>
      </div>
      <audio
        ref={audioRef}
        src={availableSongs[currentSong]?.src}
        onEnded={handleSongEnd}
        autoPlay
      />
    </div>
  );
};

export default MusicPlayer;
